import { useEffect, useState } from "react";
import { Users, MessageCircle, Edit3, X, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";
import { WHATSAPP_NUMBER } from "@/lib/api";
import { toast } from "sonner";

export default function FreelancersPage() {
  const { user } = useAuth();
  const cur = user?.preferred_currency || "GBP";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [skillIn, setSkillIn] = useState("");
  const [form, setForm] = useState({
    headline: "", bio: "", skills: [], hourly_rate: 25, currency: "GBP",
    availability: "open", location: "", portfolio_url: "", whatsapp_number: WHATSAPP_NUMBER, avatar_url: "",
  });

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    const { data } = await api.get(`/freelancers?${params.toString()}`);
    setItems(data);
  };
  const loadMine = async () => {
    try { const { data } = await api.get("/freelancers/me"); setMyProfile(data); if (data) setForm(data); }
    catch (err) { console.warn(err); }
  };
  useEffect(() => { load(); loadMine(); }, []);

  const addSkill = () => { if (!skillIn.trim()) return; setForm((s) => ({ ...s, skills: [...s.skills, skillIn.trim()] })); setSkillIn(""); };
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/freelancers/me", { ...form, hourly_rate: Number(form.hourly_rate) });
      toast.success("Profile saved");
      setEditOpen(false); loadMine(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const contact = (it) => {
    const msg = `Hi ${it.name}! Saw your Zuntria profile (${it.headline}). I'd like to discuss a project.`;
    window.open(`https://wa.me/${it.whatsapp_number}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Freelancer Hub</p>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Hire & get hired</h1>
        </div>
        <Button onClick={() => setEditOpen(true)} data-testid="fl-edit-profile">
          {myProfile ? <><Edit3 className="mr-1 h-4 w-4" /> Edit my profile</> : <><Plus className="mr-1 h-4 w-4" /> Create my profile</>}
        </Button>
      </div>

      <div className="mt-6 flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by skill or name…" className="h-10" data-testid="fl-search" />
        <Button variant="outline" onClick={load}>Search</Button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 ? (
          <div className="col-span-3 rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            <Users className="mx-auto h-8 w-8 opacity-40" />
            <p className="mt-3">No freelancers yet. Create your profile to be first!</p>
          </div>
        ) : items.map((p) => (
          <div key={p.user_id} className="flex flex-col rounded-2xl border border-border bg-card p-5" data-testid={`fl-${p.user_id}`}>
            <h3 className="font-display text-lg">{p.name}</h3>
            <p className="mt-1 text-sm text-primary">{p.headline}</p>
            <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.bio}</p>
            <div className="mt-3 flex flex-wrap gap-1">
              {(p.skills || []).slice(0, 5).map((s) => <span key={s} className="rounded-full bg-secondary px-2 py-0.5 text-xs">{s}</span>)}
            </div>
            <div className="mt-3 text-sm">
              <span className="font-display text-xl text-accent">{formatMoney(p.hourly_rate, cur)}</span>
              <span className="text-xs text-muted-foreground"> / hour · {p.availability.replace("_", " ")}</span>
            </div>
            <Button onClick={() => contact(p)} className="mt-4 bg-[#25D366] hover:bg-[#1ebd5b]" data-testid={`fl-contact-${p.user_id}`}><MessageCircle className="mr-1 h-4 w-4" /> Message on WhatsApp</Button>
          </div>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="fl-edit-dialog">
          <DialogHeader><DialogTitle>{myProfile ? "Edit profile" : "Create freelancer profile"}</DialogTitle><DialogDescription>Public profile — clients message you on WhatsApp.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <Field label="Headline"><Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} required minLength={5} className="h-11" data-testid="fl-headline" /></Field>
            <Field label="Bio"><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} required minLength={20} rows={3} data-testid="fl-bio" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={`Hourly rate (${cur})`}><Input type="number" min="0" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="h-11" data-testid="fl-rate" /></Field>
              <Field label="Availability">
                <Select value={form.availability} onValueChange={(v) => setForm({ ...form, availability: v })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open to work</SelectItem>
                    <SelectItem value="part_time">Part-time</SelectItem>
                    <SelectItem value="full_time">Full-time</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Skills">
              <div className="flex gap-2"><Input value={skillIn} onChange={(e) => setSkillIn(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} placeholder="e.g. React" className="h-10" data-testid="fl-skill-input" /><Button type="button" variant="outline" onClick={addSkill}><Plus className="h-4 w-4" /></Button></div>
              {form.skills.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {form.skills.map((s) => (
                    <span key={s} className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs">
                      {s}<button type="button" onClick={() => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }))}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </Field>
            <Field label="Location (optional)"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="h-11" /></Field>
            <Field label="Portfolio URL (optional)"><Input type="url" value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} className="h-11" placeholder="https://…" /></Field>
            <Field label="WhatsApp (with country code)"><Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} required className="h-11" data-testid="fl-whatsapp" /></Field>
            <Button type="submit" className="h-11 w-full" data-testid="fl-save">{myProfile ? "Update" : "Publish"} profile</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
